/**
 * Die Entitaeten der StoreOS-Datenbank als zod-Schemas. Spaltennamen = Feldnamen
 * (snake_case), damit eine DB-Zeile ohne Umbenennung durch `schema.parse(row)`
 * laeuft. Das ist die Form, die die API nach aussen gibt und die SPA liest.
 *
 * Tabellen: packages/db/migrations/001_grundlage.sql. Wer hier ein Feld
 * ergaenzt, ergaenzt es dort (neue Migration) und umgekehrt.
 *
 * Mandant: JEDE fachliche Zeile traegt `brand_id` (Marke) und, wo sie aus
 * einem Kanal stammt, `store_id` (Verkaufskanal). Ruling R7.
 */
import { z } from 'zod';
import { adresseSchema, dropPointSchema, PARSE_QUALITAET } from './adresse.js';
import {
  AUFTRAG_STATUS, BEWEGUNGS_GRUENDE, CARRIER_STATUS, KLAERFALL_GRUENDE, KORREKTUR_GRUENDE,
  PRODUKTIONS_STATUS, RETOURE_STATUS, RETOURE_ZUSTAENDE, ROUTE_QUELLEN, ROUTEN, SENDUNG_STATUS, ZAHLUNGSSTATUS,
} from './status.js';
import { STEUERFAELLE, STEUERKLASSEN } from './steuer.js';

const uuid = z.string().uuid();
const ts = z.coerce.date();
const cents = z.number().int();
const jsonb = z.unknown();

// ── Mandant, Kanal, Aussteller ────────────────────────────────────────────

/** Rechtsträger, der Belege ausstellt (heute N&G Einzelunternehmen, bald Schwarze Zunft GmbH). */
export const issuerSchema = z.object({
  id: uuid,
  code: z.string().min(1),
  name: z.string().min(1),
  adresse: adresseSchema.pick({ strasse: true, hausnummer: true, plz: true, ort: true, land: true }),
  email: z.string().nullable(),
  telefon: z.string().nullable(),
  ust_id: z.string().nullable(),
  steuernummer: z.string().nullable(),
  register: z.string().nullable(),
  geschaeftsfuehrung: z.string().nullable(),
  bank: z.object({ iban: z.string(), bic: z.string().nullable(), bank: z.string() }).nullable(),
  kleinunternehmer: z.boolean().default(false),
  gueltig_ab: z.string(),
  created_at: ts,
});
export type Issuer = z.infer<typeof issuerSchema>;

export const brandSchema = z.object({
  id: uuid,
  code: z.string().min(1),
  name: z.string().min(1),
  issuer_id: uuid,
  absender_name: z.string(),
  absender_email: z.string(),
  /** Absenderadresse fuer Labels und Retouren. */
  versand_absender: adresseSchema,
  einstellungen: z.record(z.string(), jsonb).default({}),
  aktiv: z.boolean(),
  created_at: ts,
});
export type Brand = z.infer<typeof brandSchema>;

export const STORE_TYPEN = ['woo', 'mock', 'manuell'] as const;
export const storeSchema = z.object({
  id: uuid,
  brand_id: uuid,
  code: z.string().min(1),
  name: z.string().min(1),
  typ: z.enum(STORE_TYPEN),
  basis_url: z.string().nullable(),
  /** Cursor fuer modified_after-Polling. */
  sync_cursor: ts.nullable(),
  letzter_sync_at: ts.nullable(),
  einstellungen: z.record(z.string(), jsonb).default({}),
  aktiv: z.boolean(),
  created_at: ts,
});
export type Store = z.infer<typeof storeSchema>;

// ── Benutzer, Audit ───────────────────────────────────────────────────────

export const ROLLEN = ['lager', 'leitung', 'admin', 'produktion'] as const;
export type Rolle = (typeof ROLLEN)[number];
/**
 * Rang fuer `requireRole` (R22). `produktion` (R127, Druckerei B&B) steht AUSSERHALB der Rangfolge: Rang 0, also unter
 * lager. Was sie darf, legt allein die Freigabeliste in apps/api/src/auth/rechte.ts fest (Produktion erfassen).
 */
export const ROLLEN_RANG: Record<Rolle, number> = { produktion: 0, lager: 1, leitung: 2, admin: 3 };

export const userSchema = z.object({
  id: uuid,
  brand_id: uuid.nullable(),
  email: z.string().email(),
  name: z.string().min(1),
  rolle: z.enum(ROLLEN),
  aktiv: z.boolean(),
  created_at: ts,
});
export type User = z.infer<typeof userSchema>;

export const auditLogSchema = z.object({
  id: uuid,
  benutzer_id: uuid.nullable(),
  aktion: z.string(),
  entitaet: z.string(),
  entitaet_id: z.string(),
  vorher: jsonb.nullable(),
  nachher: jsonb.nullable(),
  grund: z.string().nullable(),
  created_at: ts,
});
export type AuditLog = z.infer<typeof auditLogSchema>;

// ── Artikel, Lager ────────────────────────────────────────────────────────

export const warehouseSchema = z.object({
  id: uuid,
  code: z.string(),
  name: z.string(),
  adresse: adresseSchema.nullable(),
  created_at: ts,
});
export type Warehouse = z.infer<typeof warehouseSchema>;

export const BIN_TYPEN = ['pick', 'ueberlager', 'sonder'] as const;
export const binSchema = z.object({
  id: uuid,
  warehouse_id: uuid,
  /** `Regal-Fach-Ebene`, z. B. B-04-2; Sonderplaetze WE, RET, PROD, KLAER. */
  code: z.string().min(1),
  typ: z.enum(BIN_TYPEN),
  laufreihenfolge: z.number().int(),
  created_at: ts,
});
export type Bin = z.infer<typeof binSchema>;

export const articleSchema = z.object({
  id: uuid,
  brand_id: uuid,
  sku: z.string().min(1),
  name: z.string().min(1),
  variante: z.string().nullable(),
  ean: z.string().nullable(),
  route: z.enum(ROUTEN),
  /** Herkunft der Route (R100): `manuell` ueberschreibt kein Abgleich. */
  route_quelle: z.enum(ROUTE_QUELLEN),
  steuerklasse: z.enum(STEUERKLASSEN),
  /** null = unbekannt (Woo leer oder 0, R100); die Packstation verlangt dann Wiegen (R104). */
  gewicht_g: z.number().int().nullable(),
  /** Abgeleitet: gewicht_g IS NULL. */
  gewicht_unbekannt: z.boolean(),
  laenge_mm: z.number().int().nullable(),
  breite_mm: z.number().int().nullable(),
  hoehe_mm: z.number().int().nullable(),
  /** false bei Partner-/On-Demand-Ware: kein Bestand, keine Reservierung. */
  bestand_fuehren: z.boolean(),
  meldebestand: z.number().int().nullable(),
  standard_bin_id: uuid.nullable(),
  /** Einstufige Stueckliste: Komponente, die beim Verkauf reserviert wird (z. B. Innenbuch des Freundebuchs). */
  komponente_article_id: uuid.nullable(),
  komponente_menge: z.number().int().nullable(),
  bild_url: z.string().nullable(),
  aktiv: z.boolean(),
  /** R126: Hauptkategorie aus dem Kanal (erste Woo-Kategorie), null = keine (z. B. interne Artikel). */
  kategorie: z.string().nullable().optional(),
  /** R126: Verkaufspreis brutto aus dem Kanal in Cents (Sortierung/Warenwert der Erstinventur), null = unbekannt. */
  preis_cents: z.number().int().nullable().optional(),
  /** R126: Artikel wird nicht fortgefuehrt (Datum); null = aktiv im Sortiment. */
  ausgelaufen_at: ts.nullable().optional(),
  /** R127: Modul wird bei B&B on demand gedruckt; nur solche Artikel bucht die Rolle produktion als Zugang. */
  on_demand_druck: z.boolean().optional(),
  created_at: ts,
  updated_at: ts,
});
export type Article = z.infer<typeof articleSchema>;

/** Verknuepfung Artikel ↔ Kanal-Produkt (Quell-ID je Store). Ohne Eintrag kein Bestandsabgleich. */
export const channelListingSchema = z.object({
  id: uuid,
  store_id: uuid,
  article_id: uuid,
  external_product_id: z.string(),
  external_variation_id: z.string().nullable(),
  bestand_sync: z.boolean(),
  letzter_push_wert: z.number().int().nullable(),
  letzter_push_at: ts.nullable(),
  /** Letzter im Kanal gesehener Bestand (zur Erkennung von Fremdaenderungen). */
  kanal_bestand: z.number().int().nullable(),
  created_at: ts,
});
export type ChannelListing = z.infer<typeof channelListingSchema>;

export const stockMovementSchema = z.object({
  id: uuid,
  article_id: uuid,
  bin_id: uuid.nullable(),
  /** Vorzeichenbehaftet. Bestand = SUM(menge) je Artikel (und je Platz). */
  menge: z.number().int(),
  grund: z.enum(BEWEGUNGS_GRUENDE),
  korrektur_grund: z.enum(KORREKTUR_GRUENDE).nullable(),
  ref_typ: z.string().nullable(),
  ref_id: z.string().nullable(),
  bemerkung: z.string().nullable(),
  benutzer_id: uuid.nullable(),
  created_at: ts,
});
export type StockMovement = z.infer<typeof stockMovementSchema>;

export const RESERVIERUNG_STATUS = ['aktiv', 'freigegeben', 'verbraucht'] as const;
export const reservationSchema = z.object({
  id: uuid,
  article_id: uuid,
  order_item_id: uuid,
  menge: z.number().int().positive(),
  status: z.enum(RESERVIERUNG_STATUS),
  created_at: ts,
  beendet_at: ts.nullable(),
});
export type Reservation = z.infer<typeof reservationSchema>;

/** Abgeleitete Sicht (View `stock_levels`): physisch = Σ Bewegungen, reserviert = Σ aktive Reservierungen. */
export const stockLevelSchema = z.object({
  article_id: uuid,
  physisch: z.number().int(),
  reserviert: z.number().int(),
  verfuegbar: z.number().int(),
});
export type StockLevel = z.infer<typeof stockLevelSchema>;

export const packagingSchema = z.object({
  id: uuid,
  nr: z.number().int(),
  name: z.string(),
  innen_l_mm: z.number().int(),
  innen_b_mm: z.number().int(),
  innen_h_mm: z.number().int(),
  leergewicht_g: z.number().int(),
  aktiv: z.boolean(),
  /** Migration 501 (Versandrouting): Außenmaß = Innenmaß + Aufschlag, gemessene Außenmaße überschreiben. */
  aussen_aufschlag_mm: z.number().int().optional(),
  aussen_l_mm: z.number().int().nullable().optional(),
  aussen_b_mm: z.number().int().nullable().optional(),
  aussen_h_mm: z.number().int().nullable().optional(),
});
export type Packaging = z.infer<typeof packagingSchema>;

// ── Auftrag ───────────────────────────────────────────────────────────────

export const orderSchema = z.object({
  id: uuid,
  store_id: uuid,
  brand_id: uuid,
  external_id: z.string(),
  bestellnummer: z.string(),
  /** Roher Kanalstatus (Woo: processing, completed, …). */
  kanal_status: z.string(),
  zahlungsstatus: z.enum(ZAHLUNGSSTATUS),
  bestellt_at: ts,
  bezahlt_at: ts.nullable(),
  kanal_geaendert_at: ts,
  waehrung: z.string().length(3),
  summe_brutto_cents: cents,
  versand_brutto_cents: cents,
  rabatt_brutto_cents: cents,
  /** Steuer laut Kanal (Woo-Berechnung, kann bei AT falsch sein). Der Beleg rechnet selbst. */
  steuer_kanal_cents: cents,
  erstattet_cents: cents,
  zahlart: z.string().nullable(),
  zahlart_titel: z.string().nullable(),
  transaktion_id: z.string().nullable(),
  zahlungsgebuehr_cents: cents.nullable(),
  kunde_email: z.string().nullable(),
  kunde_external_id: z.string().nullable(),
  ist_gast: z.boolean(),
  /** created_via ng-fb-e2e/ng-ab-e2e u. ä.: wird importiert, aber nie bearbeitet. */
  ist_test: z.boolean(),
  kundenhinweis: z.string().nullable(),
  lieferland: z.string(),
  /** NULL = Lieferland unbekannt oder leer (W3): kein Platzhalter, Sendungen im Klaerfall `adresse`. */
  steuerfall: z.enum(STEUERFAELLE).nullable(),
  versandart: z.string().nullable(),
  /** Kleine Auswahl an Kanal-Metadaten (z. B. _ng_pd_state, Stripe-Intent). */
  kanal_meta: z.record(z.string(), jsonb).default({}),
  created_at: ts,
  updated_at: ts,
});
export type Order = z.infer<typeof orderSchema>;

export const orderItemSchema = z.object({
  id: uuid,
  order_id: uuid,
  external_item_id: z.string(),
  position: z.number().int(),
  article_id: uuid.nullable(),
  /** Eingefroren beim Import (Woo speichert keine SKU an der Position, Recherche 03 Falle 1). */
  sku_snapshot: z.string().nullable(),
  name_snapshot: z.string(),
  variante_snapshot: z.string().nullable(),
  external_product_id: z.string().nullable(),
  external_variation_id: z.string().nullable(),
  menge: z.number().int().positive(),
  einzel_brutto_cents: cents,
  gesamt_brutto_cents: cents,
  rabatt_brutto_cents: cents,
  steuer_kanal_cents: cents,
  steuerklasse: z.enum(STEUERKLASSEN),
  route: z.enum(ROUTEN),
  produktions_status: z.enum(PRODUKTIONS_STATUS),
  /** Attribute (pa_*, lokale) als Liste {name, wert}. */
  attribute: z.array(z.object({ name: z.string(), wert: z.string() })).default([]),
  /** Personalisierungsdaten als Referenz (z. B. _ng_cover_spec + Status + PDF-Pfad). Nie loggen. */
  personalisierung: jsonb.nullable(),
});
export type OrderItem = z.infer<typeof orderItemSchema>;

export const ADRESS_TYPEN = ['rechnung', 'lieferung'] as const;
export const addressSchema = adresseSchema.extend({
  id: uuid,
  /** Gespeichert wird, was der Kanal lieferte – auch leer oder kein ISO-Code (W3: Klaerfall, kein ZodError beim Lesen). */
  land: z.string(),
  order_id: uuid,
  typ: z.enum(ADRESS_TYPEN),
  roh_adresse1: z.string(),
  roh_adresse2: z.string(),
  parse_qualitaet: z.enum(PARSE_QUALITAET),
  drop_point: dropPointSchema.nullable(),
  /** Jede Korrektur erhoeht die Version; die Vorversion steht im Audit-Log. */
  version: z.number().int(),
  korrigiert_von: uuid.nullable(),
  korrigiert_at: ts.nullable(),
});
export type Address = z.infer<typeof addressSchema>;

// ── Sendung, Paket ────────────────────────────────────────────────────────

export const SENDUNG_ROUTEN = ['lager', 'partner'] as const;
export const shipmentSchema = z.object({
  id: uuid,
  order_id: uuid,
  brand_id: uuid,
  /** 1..n je Auftrag; Kennung auf Lieferschein/Mail: <Bestellnummer>-<nummer>. */
  nummer: z.number().int().positive(),
  route: z.enum(SENDUNG_ROUTEN),
  status: z.enum(SENDUNG_STATUS),
  status_vor_klaerfall: z.enum(SENDUNG_STATUS).nullable(),
  klaerfall_grund: z.enum(KLAERFALL_GRUENDE).nullable(),
  klaerfall_text: z.string().nullable(),
  warehouse_id: uuid.nullable(),
  arbeitsplatz: z.string().nullable(),
  packaging_id: uuid.nullable(),
  gewicht_g: z.number().int().nullable(),
  carrier: z.string().nullable(),
  versandprodukt: z.string().nullable(),
  partner_name: z.string().nullable(),
  bereit_at: ts.nullable(),
  gepackt_at: ts.nullable(),
  uebergeben_at: ts.nullable(),
  zugestellt_at: ts.nullable(),
  storniert_at: ts.nullable(),
  storno_grund: z.string().nullable(),
  /** Label-Scan an der Packstation (Kennzahl „Label bis naechster Auftrag“), Migration 100. */
  label_bestaetigt_at: ts.nullable(),
  /** In welchem Tagesabschluss die Sendung uebergeben wurde, Migration 100. */
  tagesabschluss_id: uuid.nullable(),
  /** Gewicht an der Packstation eingetragen bzw. Vorschlag mit Enter uebernommen (R41, Migration 101). Erst dann gibt es ein Label. */
  gewicht_bestaetigt_at: ts.nullable().optional(),
  gewicht_quelle: z.enum(['vorschlag', 'eingetragen']).nullable().optional(),
  created_at: ts,
  updated_at: ts,
});
export type Shipment = z.infer<typeof shipmentSchema>;

/** Protokoll eines Tagesabschlusses (manuell an der Packstation oder automatisch, Migration 100). */
export const tagesabschlussSchema = z.object({
  id: uuid,
  brand_id: uuid,
  /** Kalendertag YYYY-MM-DD (lokale Serverzeit). */
  datum: z.string(),
  anzahl: z.number().int(),
  erwartet_anzahl: z.number().int().nullable(),
  modus: z.enum(['manuell', 'auto']),
  benutzer_id: uuid.nullable(),
  hinweis: z.string().nullable(),
  created_at: ts,
});
export type Tagesabschluss = z.infer<typeof tagesabschlussSchema>;

export const shipmentItemSchema = z.object({
  id: uuid,
  shipment_id: uuid,
  order_item_id: uuid,
  menge: z.number().int().positive(),
  gepackt_menge: z.number().int(),
  override_grund: z.string().nullable(),
  /** Wie die Position bestaetigt wurde (R36, Migration 101): scan (EAN/SKU), fach (Fach-Etikett), tippen, enter, laufzettel, override. */
  bestaetigt_per: z.enum(['scan', 'fach', 'tippen', 'enter', 'laufzettel', 'override']).nullable().optional(),
});
export type ShipmentItem = z.infer<typeof shipmentItemSchema>;

export const LABEL_FORMATE = ['pdf_a6_100x150', 'pdf_a5', 'pdf_a4', 'zpl_100x150'] as const;
export type LabelFormat = (typeof LABEL_FORMATE)[number];

export const parcelSchema = z.object({
  id: uuid,
  shipment_id: uuid,
  carrier: z.string(),
  ist_mock: z.boolean(),
  carrier_shipment_id: z.string(),
  tracking_nummer: z.string(),
  tracking_url: z.string().nullable(),
  label_format: z.enum(LABEL_FORMATE),
  /** Label-Bytes liegen in der DB (Recherche 02: DHL-URLs verfallen). In Listen nie mitladen. */
  hat_label_pdf: z.boolean(),
  hat_label_zpl: z.boolean(),
  hat_retoure_label: z.boolean(),
  preis_cents: cents.nullable(),
  carrier_status: z.enum(CARRIER_STATUS),
  letztes_event_at: ts.nullable(),
  storniert_at: ts.nullable(),
  created_at: ts,
});
export type Parcel = z.infer<typeof parcelSchema>;

export const trackingEventSchema = z.object({
  id: uuid,
  parcel_id: uuid,
  at: ts,
  status: z.enum(CARRIER_STATUS),
  raw_status: z.string(),
  ort: z.string().nullable(),
  beschreibung: z.string().nullable(),
  created_at: ts,
});
export type TrackingEvent = z.infer<typeof trackingEventSchema>;

// ── Retoure ───────────────────────────────────────────────────────────────

export const returnSchema = z.object({
  id: uuid,
  order_id: uuid,
  brand_id: uuid,
  nummer: z.string(),
  status: z.enum(RETOURE_STATUS),
  grund: z.string().nullable(),
  quelle: z.enum(['widerruf', 'manuell', 'zustellproblem']),
  angemeldet_at: ts,
  frist_at: ts.nullable(),
  eingegangen_at: ts.nullable(),
  erstattung_cents: cents.nullable(),
  erstattet_at: ts.nullable(),
  bemerkung: z.string().nullable(),
  created_at: ts,
  updated_at: ts,
});
export type Return = z.infer<typeof returnSchema>;

export const returnItemSchema = z.object({
  id: uuid,
  return_id: uuid,
  order_item_id: uuid,
  menge: z.number().int().positive(),
  zustand: z.enum(RETOURE_ZUSTAENDE).nullable(),
  bin_id: uuid.nullable(),
  bemerkung: z.string().nullable(),
});
export type ReturnItem = z.infer<typeof returnItemSchema>;

// ── Belege ────────────────────────────────────────────────────────────────

export const BELEG_ARTEN = ['rechnung', 'gutschrift', 'lieferschein'] as const;
export type BelegArt = (typeof BELEG_ARTEN)[number];

/** RE Rechnung, GS Gutschrift (eigener Kreis seit Migration 420, R106; vorher im Kreis RE), LS Lieferschein. */
export const NUMMERNKREISE = ['RE', 'GS', 'LS'] as const;
export type Nummernkreis = (typeof NUMMERNKREISE)[number];

/** Kopf eines Belegs ohne Bytes (fuer Listen). Die Daten selbst: `belegSchema` in beleg.ts. */
export const documentKopfSchema = z.object({
  id: uuid,
  brand_id: uuid,
  issuer_id: uuid,
  order_id: uuid.nullable(),
  art: z.enum(BELEG_ARTEN),
  nummer: z.string(),
  ist_test: z.boolean(),
  datum: z.string(),
  brutto_cents: cents,
  bezug_document_id: uuid.nullable(),
  hat_xml: z.boolean(),
  erzeugt_von: z.string(),
  created_at: ts,
});
export type DocumentKopf = z.infer<typeof documentKopfSchema>;

export const numberSequenceSchema = z.object({
  issuer_id: uuid,
  kreis: z.enum(NUMMERNKREISE),
  jahr: z.number().int(),
  ist_test: z.boolean(),
  letzte: z.number().int(),
});
export type NumberSequence = z.infer<typeof numberSequenceSchema>;

// ── Sync, Outbox, Inbox ───────────────────────────────────────────────────

export const SYNC_RICHTUNG = ['import', 'export'] as const;
export const SYNC_ERGEBNIS = ['ok', 'uebersprungen', 'warnung', 'fehler'] as const;
export const syncLogSchema = z.object({
  id: uuid,
  store_id: uuid.nullable(),
  richtung: z.enum(SYNC_RICHTUNG),
  art: z.string(),
  external_id: z.string().nullable(),
  ergebnis: z.enum(SYNC_ERGEBNIS),
  meldung: z.string(),
  /** Keine personenbezogenen Daten hier ablegen (Ruling R5). */
  details: jsonb.nullable(),
  created_at: ts,
});
export type SyncLog = z.infer<typeof syncLogSchema>;

export const OUTBOX_ZIELE = ['woo', 'carrier', 'mail', 'buchhaltung', 'belege', 'druck'] as const;
export const OUTBOX_STATUS = ['offen', 'trockenlauf', 'erledigt', 'fehlgeschlagen'] as const;
export const outboxSchema = z.object({
  id: uuid,
  ziel: z.enum(OUTBOX_ZIELE),
  /** z. B. woo.order_status, woo.order_note, woo.stock, mail.versand, belege.rechnung */
  typ: z.string(),
  brand_id: uuid.nullable(),
  store_id: uuid.nullable(),
  /** Fachlicher Schluessel, verhindert Doppelauftraege (z. B. woo.order_status:<order_id>:completed). */
  idempotenz_key: z.string(),
  payload: jsonb,
  status: z.enum(OUTBOX_STATUS),
  versuche: z.number().int(),
  naechster_versuch_at: ts.nullable(),
  letzter_fehler: z.string().nullable(),
  ergebnis: jsonb.nullable(),
  created_at: ts,
  erledigt_at: ts.nullable(),
});
export type Outbox = z.infer<typeof outboxSchema>;

export const webhookInboxSchema = z.object({
  id: uuid,
  store_id: uuid.nullable(),
  quelle: z.enum(['woo', 'shipcloud', 'dhl', 'mock', 'partner']),
  topic: z.string(),
  delivery_id: z.string(),
  signatur_ok: z.boolean(),
  payload: jsonb,
  verarbeitet_at: ts.nullable(),
  fehler: z.string().nullable(),
  created_at: ts,
});
export type WebhookInbox = z.infer<typeof webhookInboxSchema>;

export const mailLogSchema = z.object({
  id: uuid,
  brand_id: uuid,
  order_id: uuid.nullable(),
  /** Fachlicher Schluessel (z. B. mail.versand:<shipment_id>, rechnung:<document_id>); unique, verhindert Doppelversand. */
  idempotenz_key: z.string(),
  an: z.string(),
  betreff: z.string(),
  text: z.string(),
  html: z.string().nullable(),
  anhaenge: z.array(z.object({ dateiname: z.string(), typ: z.string(), bytes: z.number().int() })).default([]),
  modus: z.enum(['mock', 'live']),
  status: z.enum(['gesendet', 'fehler']),
  fehler: z.string().nullable(),
  created_at: ts,
});
export type MailLog = z.infer<typeof mailLogSchema>;

export const accountingExportSchema = z.object({
  id: uuid,
  brand_id: uuid,
  document_id: uuid,
  adapter: z.string(),
  modus: z.enum(['mock', 'live']),
  status: z.enum(['offen', 'uebertragen', 'fehler']),
  request: jsonb,
  response: jsonb.nullable(),
  created_at: ts,
});
export type AccountingExport = z.infer<typeof accountingExportSchema>;

// ── API-Sichten (zusammengesetzt) ─────────────────────────────────────────

export const auftragStatusSchema = z.enum(AUFTRAG_STATUS);

/** Auftrag mit allem, was das Auftragsdetail zeigt. */
export const orderDetailSchema = orderSchema.extend({
  status: auftragStatusSchema,
  positionen: z.array(orderItemSchema),
  adressen: z.array(addressSchema),
  sendungen: z.array(shipmentSchema.extend({ positionen: z.array(shipmentItemSchema), pakete: z.array(parcelSchema) })),
  belege: z.array(documentKopfSchema),
  retouren: z.array(returnSchema),
});
export type OrderDetail = z.infer<typeof orderDetailSchema>;
